import style from "./style.module.scss";

import React, { cloneElement, ReactNode, useEffect, useState, useRef } from "react";
import classnames from "classnames";
import { Icon, jnjArrowLeft, jnjArrowRight, jnjLock } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import ResizeObserver from "@zeecoder/react-resize-observer";
import { mapBreakpoints, BreakpointMap } from "../../../utils/breakpoints";
import { MediaStatus } from "@organisms/VideoPlayer/MediaStatus";
import cleanCSSIdentifier from "@jmc/utils/utils/clean-css-identifier";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { getLocaleRtl } from "@jmc/solid-design-system/src/utils/languages";
export enum ImagePosition {
    TOP_LEFT = "top-left",
    TOP_CENTER = "top-center",
    TOP_RIGHT = "top-right",
    CENTER_LEFT = "center-left",
    CENTER_CENTER = "center-center",
    CENTER_RIGHT = "center-right",
    BOTTOM_LEFT = "bottom-left",
    BOTTOM_CENTER = "bottom-center",
    BOTTOM_RIGHT = "bottom-right",
}

export type ImagePlacement = "default" | "50_img_left" | "50_img_right"; // TODO: Remove after rebranding
export type HorizontalAlignment = "start" | "center" | "end"; // TODO: Remove after rebranding
export type Padding = "default" | "s" | "m" | "l"; // TOOD: Remove after rebranding

interface Props {
    image?: string | JSX.Element;
    alt?: string;
    imageFit?: "cover" | "contain";
    imagePosition?: ImagePosition;
    video?: JSX.Element;
    banner?: ReactNode;
    info?: string | JSX.Element | JSX.Element[];
    title?: string | JSX.Element | JSX.Element[];
    additionalInfo?: string | JSX.Element | JSX.Element[];
    tags?: JSX.Element | JSX.Element[];
    icon?: string;
    fullBackground?: boolean; // TODO: Remove after rebranding
    dropShadow?: boolean; // TODO: Remove after rebranding
    isClickable?: boolean;
    footer?: JSX.Element | JSX.Element[];
    children?: React.ReactNode;
    mediastop?: (status: MediaStatus, currentTime: number | string) => void;
    getLength?: (duration: number) => void;
    cookieAccept?: (accepted: boolean) => void;
    getCurrentTime?: (currentTime: number) => void;
    checkCardPosition?: (horizontal: boolean) => void;
    horizontalAlignment?: HorizontalAlignment; // TODO: Remove after rebranding
    imagePlacement?: ImagePlacement;
    padding?: Padding; // TODO: Remove after rebranding
    verticalLayout?: boolean;
    imageRatio?: "hd" | "polyvision";
    margin?: "default" | "s" | "m" | "l" | "xl"; // TODO: Remove after rebranding
    idPrefix?: string;
    id?: string;
    cookieMessage?: JSX.Element;
    loading?: "eager" | "lazy";
    addBorder?: boolean; // TODO: Remove after rebranding
    fallbackImageComponent?: JSX.Element;
    isTransparent?: boolean; // TODO: Remove after rebranding. There should be a background color prop instead
    publicationDate?: JSX.Element;
    contentLinksNotUnderlined?: boolean;
    isOnDetailPage?: boolean;
    removeContentInlineMargin?: boolean;
}

const CardBreakpoints: BreakpointMap = {
    "card-vertical": 0,
    "card-horizontal": 640,
};

export const CTAIconArrow = ({ color = "inherit" }: { color?: "inherit" | "white" }) => {
    const locale = useLocale();
    const rtl = getLocaleRtl(locale);
    return <Icon icon={rtl ? jnjArrowLeft : jnjArrowRight} type="jnj" color={color} size="medium" />;
};

export const CTAIconLock = ({ color = "inherit", className }: { color?: "inherit" | "white"; className?: string }) => {
    return <Icon data-test-id="cardLock" className={className} icon={jnjLock} type="jnj" color={color} size="small" />;
};

export const Card = ({
    image = null,
    alt,
    video = null,
    banner = null,
    info = null,
    title,
    additionalInfo = null,
    tags = null,
    fullBackground = false,
    dropShadow = true,
    isClickable = false,
    footer,
    icon,
    imageFit = "cover",
    imagePosition = ImagePosition.CENTER_CENTER,
    fallbackImageComponent,
    children,
    mediastop = null,
    getLength = null,
    checkCardPosition,
    cookieAccept = null,
    getCurrentTime = null,
    horizontalAlignment = "start",
    imagePlacement = "default",
    padding = "default",
    verticalLayout = false,
    imageRatio = "hd",
    margin = "default",
    idPrefix = "Card",
    id = "",
    cookieMessage = null,
    loading = null,
    addBorder = false,
    isTransparent = false,
    publicationDate = null,
    contentLinksNotUnderlined = false,
    isOnDetailPage = false,
    removeContentInlineMargin = false,
    ...other
}: Props) => {
    const { jnjFullBranded } = useJnjBranding();
    const [hasCTA, setHasCTA] = useState(false);
    const [cardRef, setCardRef] = useState(null);
    const footerRef = useRef(null);

    useEffect(() => {
        // If the card does not have a CTA link in the footer, it should not be clickable
        const cardFirstCTA = footerRef?.current?.querySelector('a, button, [role="button"]');
        if (cardFirstCTA && isClickable && jnjFullBranded && cardRef && footerRef) {
            setHasCTA(true);

            const cardClickHandle = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                // If the user clicked on a link or a button (tooltip, tags, etc), allow the default behavior
                // Otherwise, simulate a click on the first CTA
                if (
                    (e.target as HTMLElement).closest("a") == null &&
                    (e.target as HTMLElement).closest("button") == null &&
                    (e.target as HTMLElement).closest('[role="button"]') == null
                ) {
                    e.preventDefault();
                    e.stopPropagation();
                    cardFirstCTA?.click();
                }
            };
            cardRef.addEventListener("click", cardClickHandle);

            return () => {
                cardRef.removeEventListener("click", cardClickHandle);
            };
        }
    }, [cardRef, footerRef]);

    return (
        <ResizeObserver>
            {(ref: React.RefObject<HTMLDivElement>, width: number) => {
                if (cardRef === null && ref?.current !== null) {
                    setCardRef(ref?.current);
                }

                const breakpoints = mapBreakpoints(width, CardBreakpoints);
                // A card is displayed in the horizontal layout if the width is at least 640 px
                if (!video && checkCardPosition) checkCardPosition(width >= CardBreakpoints["card-horizontal"]);
                const horizontalLayout = verticalLayout ? false : width >= CardBreakpoints["card-horizontal"];
                const verticalBreakpoint = width < CardBreakpoints["card-horizontal"];

                const titleString = title && typeof title === "string" ? title?.toLowerCase() : id;

                return (
                    <div
                        ref={ref}
                        className={classnames(
                            style.card,
                            addBorder ? style.addBorder : null,
                            horizontalLayout ? style["card-horizontal"] : style["card-vertical"],
                            horizontalLayout && imagePlacement
                                ? style[`card-horizontal__imgPlacement_${imagePlacement}`]
                                : style["card-horizontal__imgPlacement_default"],
                            horizontalAlignment
                                ? style[`card-horizontal_alignment__${horizontalAlignment}`]
                                : style["card-horizontal_alignment__start"],
                            !dropShadow && style["card-withoutDropshadow"],
                            ((!jnjFullBranded && isClickable) || (jnjFullBranded && hasCTA && isClickable)) &&
                                style["card-isClickable"],
                            jnjFullBranded && idPrefix === "AudioCard" && style["audioCard"],
                            isOnDetailPage
                                ? null
                                : jnjFullBranded && idPrefix === "AudioCard"
                                  ? horizontalLayout
                                      ? style["audioCard__horizontal"]
                                      : style["audioCard__vertical"]
                                  : null,
                            ((image && !fullBackground) || video || cookieMessage) && style.withHeader,
                            image && fullBackground && !jnjFullBranded && style.fullBackground,
                            isTransparent && style.transparent,
                        )}
                        data-breakpoint={breakpoints[breakpoints.length - 1]}
                        {...other}
                        id={`${idPrefix}.${cleanCSSIdentifier(titleString)}`}
                    >
                        {image && fullBackground && !jnjFullBranded && (
                            <div
                                className={classnames(style.backgroundImage, style[imagePosition])}
                                style={{ backgroundImage: `url('${image}')` }}
                                data-test-id={`Card.FullBackgroundImage`}
                            />
                        )}
                        {!jnjFullBranded && fallbackImageComponent}

                        <div
                            className={
                                verticalBreakpoint
                                    ? jnjFullBranded
                                        ? style.media // TODO: Rebranding: header and media should be combined into one
                                        : null
                                    : classnames(
                                          style.header,
                                          jnjFullBranded ? style.media : null, // TODO: Rebranding: header and media should be combined into one
                                          horizontalLayout && imagePlacement
                                              ? style[`header__imgPlacement_${imagePlacement}`]
                                              : style["header__imgPlacement_default"],
                                      )
                            }
                        >
                            {verticalBreakpoint ? banner && <>{banner}</> : horizontalLayout && <>{banner}</>}
                            {jnjFullBranded && fallbackImageComponent}
                            {image && !fullBackground && (
                                <div
                                    className={classnames(
                                        style.image,
                                        style[`${imageRatio}-ratio`],
                                        verticalBreakpoint
                                            ? margin == "default"
                                                ? ""
                                                : style[`margin_${margin}`]
                                            : null,
                                    )}
                                    data-test-id={`Card.Image`}
                                >
                                    {typeof image === "string" ? (
                                        <img
                                            className={classnames(style[imageFit], style[imagePosition])}
                                            src={image}
                                            alt={alt}
                                            loading={loading}
                                        />
                                    ) : (
                                        image
                                    )}
                                </div>
                            )}
                            {video && (
                                <div className={style.video}>
                                    {cloneElement(video, {
                                        mediastop: mediastop,
                                        getLength: getLength,
                                        cookieAccept: cookieAccept,
                                        getCurrentTime: getCurrentTime,
                                    })}
                                </div>
                            )}
                            {cookieMessage}
                        </div>

                        {(title || info || additionalInfo || children || footer) && (
                            <div
                                className={classnames(
                                    style.content,
                                    removeContentInlineMargin && style["remove-inline-margin"],
                                    info && style.withInfo,
                                    horizontalLayout && imagePlacement
                                        ? style[`content__imgPlacement_${imagePlacement}`]
                                        : style["content__imgPlacement_default"],
                                    horizontalLayout && padding
                                        ? style[`content__padding_${padding}`]
                                        : style["content__padding_default"],
                                )}
                            >
                                {(title || children) && (
                                    <div className={classnames(style.body)}>
                                        {jnjFullBranded && publicationDate && (
                                            <div data-test-id={`Card.PublishInfo`}>
                                                <Typography weight="400" color="inherit" component="span">
                                                    {publicationDate}
                                                </Typography>
                                            </div>
                                        )}
                                        {info && (
                                            <div className={style.info} data-test-id={`Card.Info`}>
                                                <Typography size="s">{info}</Typography>
                                            </div>
                                        )}
                                        {title && (
                                            <div
                                                className={classnames(
                                                    style.title,
                                                    fullBackground ? style.fullBackground : null,
                                                    icon ? style.withIcon : null,
                                                )}
                                                data-test-id={`Card.Title`}
                                            >
                                                {icon && <Icon icon={icon} size="large" verticalAlign="middle" />}
                                                <Typography
                                                    variant={jnjFullBranded ? "h4" : "h5"}
                                                    weight={jnjFullBranded ? undefined : "500"}
                                                    color="inherit"
                                                    component="span"
                                                >
                                                    {title}
                                                </Typography>
                                            </div>
                                        )}
                                        {additionalInfo && (
                                            <div className={style.info} data-test-id={`Card.AdditionalInfo`}>
                                                <Typography size="s">{additionalInfo}</Typography>
                                            </div>
                                        )}
                                        {children && (
                                            <div
                                                className={classnames(
                                                    style.description,
                                                    contentLinksNotUnderlined ? style["custom-link"] : null,
                                                )}
                                                data-test-id={`Card.Description`}
                                            >
                                                {children}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {tags && (
                                    <div className={style.tags} data-test-id={`Card.Tags`}>
                                        {tags}
                                    </div>
                                )}
                                {footer && (
                                    <div ref={footerRef} className={style.footer} data-test-id={`Card.Footer`}>
                                        {footer}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            }}
        </ResizeObserver>
    );
};

Card.displayName = "Card";
Card.CTAIconArrow = CTAIconArrow;
Card.CTAIconLock = CTAIconLock;
